import { Component, ViewChild } from '@angular/core';
import { ProjectService } from '../../_core/services/project.service';
import { ProfileService } from '../../_core/services/profile.service';
import { ProjectSpecification } from '../../_core/models/project-specification.model';
import { ApiService } from '../../_core/services/api.service';
import { ExportState, IPrintoutFile, PrintoutType } from '../../_core/models/boms/bom.model';
import { LocationService } from '../../_core/services/location.service';
import { ToolboxItemService } from '../../_core/services/toolbox-item.service';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/_core/services/auth.service';
import { CrmService } from 'src/app/_core/services/crm.service';
import { OfferComponent } from '../offer/offer.component';
import { saveAs } from '@progress/kendo-file-saver';
import { DatePipe } from '@angular/common';
import { Exporter } from 'src/app/_core/exporter';
import { GUID_EMPTY } from 'src/app/_core/constants/constants';
import { v4 as uuidv4 } from 'uuid';
import { PrintChannel } from '../svg-component/svg.component';


@Component({
  selector: 'app-export-popup',
  templateUrl: './bom-popup.component.html',
  styleUrls: ['./bom-popup.component.css']
})
export class BomPopupComponent {
  @ViewChild(OfferComponent) offerComponent: OfferComponent;
  private expectedImages: number = 0;
  private images = [];
  private excelMode: boolean = false;

  public isPopupShown: boolean = false;
  public isStatusDialogOpened: boolean = false;
  public statusDialogMessage: string = "";
  public exportInProgress: boolean;
  public exportSuccess: boolean;
  
  get showBOMExport(): boolean {
    return !this.crmService.isCrmPresent || this.canExportOnLocalhost;
  }

  get bomExportDisabled(): boolean {
    if (this.canExportOnLocalhost) {
      return false;
    }
    return this.toolBSrvc.getCurrTool() != null || !this.projectService.isValid || this.projectService.draftMode || (this.crmService.isCrmPresent && !this.projectService.readOnly);
  }

  get exportExcelDisabled(): boolean {
    if (this.canExportOnLocalhost) {
      return false;
    }
    return !this.projectService.isValid || (this.crmService.isCrmPresent && !this.projectService.readOnly);
  }

  get exportExcelLabel(): string {
		if (this.projectService.isValid) {
			if((this.projectService.readOnly && this.crmService.isCrmPresent) || !this.crmService.isCrmPresent){
				return $localize`:Toolbar|'Export to excel' button tooltip:Export to Excel`;
			} else {
				return $localize`:Toolbar|'Export to excel' button tooltip:Project is editable, export is disabled`;
			}
		} else {
			return $localize`:Toolbar|'Export to excel' button disabled tooltip:Project is not valid, export is disabled`;
		}
	}

  get exportBOMLabel(): string {
    if (this.projectService.draftMode) {
      return $localize`:Toolbar|'Export BOM' button disabled tooltip:Export to ERP is disabled in test mode`;
    }
    else if (this.projectService.isValid) {
      if((this.projectService.readOnly && this.crmService.isCrmPresent) || !this.crmService.isCrmPresent){
        return $localize`:Toolbar|'Export BOM' button tooltip:Export to ERP`;
      } else {
        return $localize`:Toolbar|'Export BOM' button tooltip:Project is editable, export is disabled`;
      }
    } else {
      return $localize`:Toolbar|'Export BOM' button disabled tooltip:Project is not valid, export is disabled`;
    }
  }
  
  private _dialogTitle: string;
  get dialogTitle() {
    return this._dialogTitle;
  }

  public get canShowExport(): boolean {
    if (environment.localhost) {
      return true;
    }
    return this.authService.user?.hasAccessToERPExport ?? false;
  }

  public get canExportOnLocalhost(): boolean {
    return environment.localhost;
  }

  constructor(
    private profileService: ProfileService,
    public projectService: ProjectService,
    private locationService: LocationService,
    public toolBSrvc: ToolboxItemService,
    private api: ApiService,
    private authService: AuthService,
    public crmService: CrmService) {
  }
  
  projectSpecification = new ProjectSpecification(this.profileService);

  ngOnInit(): void {
    this.projectService.printoutReady.subscribe((printout: { channel: PrintChannel, file: IPrintoutFile }) => {
			if (printout.channel == PrintChannel.API) {
        this.checkImagesAndSendBOM(printout.file);
			}
		});
  }

  private checkImagesAndSendBOM(printout: IPrintoutFile) {
    if (printout != null && printout.type != PrintoutType.None) {
      this.images.push({ PrintoutType: PrintoutType[printout.type], Id: printout.content });
    }

    if (this.expectedImages == 0 || this.images.length === this.expectedImages) {
      this.sendBomToApi();
    }
  }

    sendBomToApi() {
      var exp = new Exporter(this.projectService, this.locationService);
      const toSend = exp.collectBomToSend()
      const emptyImage = this.images.find(i => i.Id == GUID_EMPTY);
  
      if (emptyImage) {
        setTimeout(() => {
          this.statusDialogMessage =  $localize`:BOM|Image export error message:An error has occurred while exporting an image to ERP. Image type: ` + emptyImage.PrintoutType;
        }, 0);
        this.exportSuccess = false;
        if (!environment.localhost) {
          return;
        }
      }
  
      this.api.sendBom(this.projectService.productId, toSend, this.projectService.brandName).subscribe(e => {
        if (e == GUID_EMPTY) {
          setTimeout(() => {
            this.statusDialogMessage = $localize`:BOM|Export error message:An error has occurred while exporting to ERP. Please try again.`;
          }, 0);
          this.exportSuccess = false;
        } else {
          setTimeout(() => {
            this.statusDialogMessage = $localize`:BOM|Export success message:Data export to ERP was successful`;
          }, 0);
          this.exportSuccess = true;
        }
        this.exportInProgress = false;
      });
    }
  
  public togglePopup(show: boolean = true, excel: boolean = false) {
    this._dialogTitle = excel ? $localize`:BOM|'Export to Excel' header:Export to Excel` : $localize`:BOM|'Export BOM' header:Export to ERP`;
    this.isPopupShown = show;
    this.excelMode = excel;

    if (this.isPopupShown) {  
      if (!this.projectService.isValid && !this.canExportOnLocalhost) {
      	this.statusDialogMessage = $localize`:Toolbar|'Export BOM' button disabled tooltip:Project is not valid, export is disabled`;
      	this.exportSuccess = false;
      	this.exportInProgress = false;
      	this.isStatusDialogOpened = true;
      }
    }
  }
  
  public cancel() {
    this.closePopup();
  }

  public export() {
    this.offerComponent.changeElements("inBOM");
    if (this.excelMode) {
      this.renderExcel();
    } else {
      this.exportInProgress = true;
      this.isStatusDialogOpened = true;
      this.images = [];
      const requestId = uuidv4();
      this.expectedImages = this.projectService.calculateExpectedImages();
      this.projectSpecification.setup();                
      this.projectService.printProjectSubj.next({ requestId, channel: PrintChannel.API });      
    }
  }

  public closePopup() {
    this.isStatusDialogOpened = false;
    this.togglePopup(false);
    this.crmService.sendCloseRequest(this.exportSuccess ? ExportState.Success : ExportState.Error);
  }

  getIcon() {
    if (this.exportInProgress) {
      return;
    }
    if (this.exportSuccess) {
      return this.projectService.svgIcons.checkOutlineIcon;
    }
    return this.projectService.svgIcons.xIcon;
  }

    renderExcel() {
      var exp = new Exporter(this.projectService, this.locationService);
      var boms = exp.collectBomToSend();

      // this.bom.getIndividualElements();
      // const boms = this.bom.getBoms(PopupExportType.fullConstruction);
      const toSend = null; // this.bom.collectBomToSend(boms);
      this.api.getExcel(this.projectService.productId, this.projectService.brandName, boms).subscribe(
        (data: Blob) => {
          const oi = this.projectService.info?.orderCode ?? this.projectService.orderCode;
          const cn = this.projectService.info?.customerName;
          const dt: DatePipe = new DatePipe('en');
          const fd = dt.transform(new Date(), 'dd.MM.YYYY');
          
          const fn = cn ? `${oi} - ${cn} - ${fd}.xlsx` : `${oi} - ${fd}.xlsx`;
  
          this.projectService.showTemporaryMessageSubj.next(
            {
              message: $localize`:BOM|Export to Excel success message:Export to Excel done`, hideAfter: 5000, style: 'success'
            }
          );
          saveAs(data, fn);
          this.closePopup();
        })
    }

}
